import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMessages, markMessageAsRead, sendMessage, getConversation, getConversations, getAdmin, editMessage, deleteMessage, Message, ConversationMessage, downloadMessageAttachment } from '../services/messages';
import { uploadToSupabase } from '../services/supabase';
import { validateFileSize, MAX_FILE_SIZE_IMAGES, MAX_FILE_SIZE_DOCUMENTS } from '../utils/fileValidation';
import { useFileError } from '../contexts/FileErrorContext';
import { useTimeout } from '../contexts/TimeoutContext';
import { handleRateLimitError } from '../utils/rateLimitHandler';
import { handleUploadError } from '../utils/uploadErrorHandler';
import { AnalyticsEvents } from '../services/analytics';
import Modal from './Modal';
import './ChatWidget.css';

interface ChatWidgetProps {
  userId?: number;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ userId }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<(Message | ConversationMessage)[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [adminId, setAdminId] = useState<number | null>(() => {
    // Intentar recuperar adminId del sessionStorage
    const saved = sessionStorage.getItem('chatWidget_adminId');
    return saved ? parseInt(saved, 10) : null;
  });
  const [currentUserId, setCurrentUserId] = useState<number | undefined>(userId);
  const [isSendingReviewRequest, setIsSendingReviewRequest] = useState(false);
  const [adminName, setAdminName] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [editingMessageDimensions, setEditingMessageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [replyToMessageId, setReplyToMessageId] = useState<number | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<number | null>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const openChatButtonRef = useRef<HTMLButtonElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);
  const { showFileSizeError } = useFileError();
  const { showTimeout } = useTimeout();

  // Persistir adminId en sessionStorage cuando cambie
  useEffect(() => {
    if (adminId) {
      sessionStorage.setItem('chatWidget_adminId', adminId.toString());
    } else {
      sessionStorage.removeItem('chatWidget_adminId');
    }
  }, [adminId]);

  // Cerrar widget al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // No cerrar si el click es en el botón de abrir chat
      if (openChatButtonRef.current && openChatButtonRef.current.contains(target)) {
        return;
      }

      // No cerrar si el click es en cualquier botón dentro del widget
      if (widgetRef.current) {
        const clickedButton = target.closest('button');
        if (clickedButton && widgetRef.current.contains(clickedButton)) {
          return;
        }

        // Solo cerrar si el click es completamente fuera del widget
        if (!widgetRef.current.contains(target)) {
          setIsOpen(false);
          setIsMinimized(false);
        }
      }
    };

    if (isOpen && !isMinimized) {
      // Usar mousedown en lugar de click para evitar conflictos con la navegación
      // Agregar un pequeño delay para que el click del botón se procese primero
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 50);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, isMinimized]);

  // Cargar adminId al montar el componente para que el botón de revisión funcione
  useEffect(() => {
    if (!adminId) {
      findAdminId();
    }
  }, []);

  // Cargar mensajes cuando se abre el widget o cuando cambia adminId
  useEffect(() => {
    if (isOpen) {
      // Resetear isFirstLoad cuando se abre el widget
      setIsFirstLoad(true);
      if (adminId) {
        loadMessages();
      } else {
        findAdminId().then(() => {
          // Después de encontrar adminId, cargar mensajes
          // Esperar un momento para que el estado se actualice
          setTimeout(() => {
            loadMessages();
          }, 100);
        });
      }
    }
  }, [isOpen, adminId]);

  // Estado para rastrear si es la primera carga de mensajes
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Scroll al final - siempre en la primera carga, después solo si el usuario estaba cerca del final
  useEffect(() => {
    if (isOpen && !isMinimized && messagesContainerRef.current && messagesEndRef.current) {
      const container = messagesContainerRef.current;
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;

      // SIEMPRE hacer scroll en la primera carga, después solo si estaba cerca del final
      if (isFirstLoad || isNearBottom) {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          if (isFirstLoad && messages.length > 0) {
            setIsFirstLoad(false);
          }
        }, 100);
      }
    }
  }, [messages, isOpen, isMinimized, isFirstLoad]);

  // Cargar mensajes periódicamente cuando está abierto
  useEffect(() => {
    if (isOpen && !isMinimized) {
      const interval = setInterval(() => {
        loadMessages();
      }, 5000); // Cada 5 segundos

      return () => clearInterval(interval);
    }
  }, [isOpen, isMinimized]);

  // Cargar mensajes no leídos cuando el widget está cerrado
  useEffect(() => {
    if (!isOpen) {
      loadUnreadCount();
      const interval = setInterval(() => {
        loadUnreadCount();
      }, 10000); // Cada 10 segundos

      return () => clearInterval(interval);
    }
  }, [isOpen]);

  // Ajustar el textarea de edición para que tenga las mismas dimensiones que el mensaje original
  useEffect(() => {
    if (editingMessageId && editTextareaRef.current && editingMessageDimensions) {
      // Usar requestAnimationFrame para asegurar que el DOM esté completamente renderizado
      requestAnimationFrame(() => {
        const textarea = editTextareaRef.current;
        if (!textarea) return;

        const messageContent = textarea.closest('.chat-widget-message-content') as HTMLElement;
        if (!messageContent) return;

        // Obtener las dimensiones originales capturadas
        const originalWidth = editingMessageDimensions.width;
        const originalHeight = editingMessageDimensions.height;

        // Obtener el ancho máximo disponible del contenedor padre
        const messageContentWidth = messageContent.clientWidth;
        const messageContentPadding = 24; // 12px izquierdo + 12px derecho
        const maxAvailableWidth = messageContentWidth - messageContentPadding;

        // Obtener el ancho máximo del contenedor del widget
        const widgetContainer = messagesContainerRef.current;
        const widgetMaxWidth = widgetContainer 
          ? widgetContainer.clientWidth * 0.9 // 90% del ancho del widget
          : window.innerWidth * 0.9;

        // Usar el ancho del mensaje original, pero limitarlo para evitar desbordes
        const targetWidth = Math.min(originalWidth, maxAvailableWidth, widgetMaxWidth);
        const finalWidth = Math.max(targetWidth, 150); // mínimo 150px

        // Aplicar el ancho (el textarea tiene padding de 8px a cada lado = 16px total)
        const textareaPadding = 16;
        const textareaWidth = finalWidth + textareaPadding;
        const textareaMaxWidth = maxAvailableWidth + textareaPadding;

        textarea.style.width = `${textareaWidth}px`;
        textarea.style.maxWidth = `${textareaMaxWidth}px`;
        textarea.style.minWidth = '166px'; // 150px + 16px padding
        textarea.style.flexShrink = '0';
        textarea.style.flexGrow = '0';
        textarea.style.overflowX = 'hidden';

        // Ajustar la altura
        textarea.style.height = 'auto';
        const scrollHeight = textarea.scrollHeight;
        const finalHeight = Math.max(originalHeight, scrollHeight);
        textarea.style.height = `${finalHeight}px`;
        textarea.style.overflowY = scrollHeight > originalHeight ? 'auto' : 'hidden';

        // Forzar el word-wrap para evitar desbordes
        textarea.style.wordWrap = 'break-word';
        textarea.style.overflowWrap = 'break-word';
        textarea.style.wordBreak = 'break-word';
      });
    }
  }, [editingMessageId, editingMessageDimensions]);

  const findAdminId = async () => {
    try {
      // Primero verificar si ya tenemos adminId guardado
      const savedAdminId = sessionStorage.getItem('chatWidget_adminId');
      if (savedAdminId) {
        const adminIdNum = parseInt(savedAdminId, 10);
        if (!isNaN(adminIdNum)) {
          setAdminId(adminIdNum);
          return;
        }
      }

      // Si no hay guardado, buscar en los mensajes recibidos
      const response = await getMessages();
      const adminMessage = response.messages.find(msg => msg.fromUser.role === 'admin');
      if (adminMessage) {
        setAdminId(adminMessage.fromUser.id);
        if (adminMessage.fromUser.name) {
          setAdminName(adminMessage.fromUser.name);
        }
        sessionStorage.setItem('chatWidget_adminId', adminMessage.fromUser.id.toString());
        return;
      }

      // Si no hay mensajes del admin, intentar obtenerlo de las conversaciones
      try {
        const conversationsResponse = await getConversations();
        const adminConversation = conversationsResponse.conversations.find(conv => conv.role === 'admin');
        if (adminConversation && adminConversation.userId) {
          setAdminId(adminConversation.userId);
          if (adminConversation.name) {
            setAdminName(adminConversation.name);
          }
          sessionStorage.setItem('chatWidget_adminId', adminConversation.userId.toString());
          return;
        }
      } catch (convError) {
        // Si falla, no hay problema, intentaremos obtener el admin directamente

      }

      // Si no hay adminId en mensajes ni conversaciones, obtenerlo directamente del servidor
      try {
        const adminResponse = await getAdmin();
        if (adminResponse.success && adminResponse.admin) {
          setAdminId(adminResponse.admin.id);
          setAdminName(adminResponse.admin.name);
          sessionStorage.setItem('chatWidget_adminId', adminResponse.admin.id.toString());
          return;
        }
      } catch (adminError) {
        // Error al obtener admin directamente - silenciado
      }

      // Si no se pudo obtener adminId, el usuario no podrá enviar mensajes hasta que se resuelva
    } catch (error) {
      // Error al buscar adminId - silenciado
    }
  };

  const loadMessages = async () => {
    try {
      setLoading(true);

      // SIEMPRE intentar usar getConversation si tenemos adminId
      // Esto asegura que veamos tanto mensajes enviados como recibidos
      if (adminId) {
        try {
          const conversationResponse = await getConversation(adminId);
          // Ordenar mensajes por fecha (más antiguos primero)
          const sortedMessages = [...conversationResponse.messages].sort((a, b) => {
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          });
          setMessages(sortedMessages);

          // Guardar nombre del admin
          if (conversationResponse.otherUser) {
            const admin = conversationResponse.otherUser;
            if (admin.role === 'admin') {
              if (admin.name && admin.name !== admin.email) {
                setAdminName(admin.name);
              } else {
                setAdminName(null);
              }
            }
          }

          // Obtener contador de no leídos
          const unreadResponse = await getMessages();
          setUnreadCount(unreadResponse.unreadCount);

          // Obtener currentUserId de la respuesta si está disponible
          if (conversationResponse.messages.length > 0) {
            // Buscar cualquier mensaje del usuario actual para obtener su ID
            const userMessage = conversationResponse.messages.find(msg => msg.isFromCurrentUser);
            if (userMessage && userMessage.fromUserId) {
              setCurrentUserId(userMessage.fromUserId);
            } else if (!currentUserId && userId) {
              // Si no encontramos en los mensajes, usar el userId del prop
              setCurrentUserId(userId);
            }
          }

          // Marcar mensajes no leídos como leídos
          const unreadMessages = conversationResponse.messages.filter(msg => !msg.read && !msg.isFromCurrentUser);
          for (const message of unreadMessages) {
            try {
              await markMessageAsRead(message.id);
            } catch (error) {

            }
          }
        } catch (convError) {

          // Si falla getConversation, usar getMessages como fallback
          const response = await getMessages();
          const sortedMessages = [...response.messages].sort((a, b) => {
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          });
          setMessages(sortedMessages);
          setUnreadCount(response.unreadCount);
        }
      } else {
        // Si no hay adminId, intentar encontrarlo primero
        await findAdminId();

        // Si después de buscar encontramos adminId, usar getConversation
        // Necesitamos esperar un momento para que el estado se actualice
        const currentAdminId = adminId || (sessionStorage.getItem('chatWidget_adminId') ? parseInt(sessionStorage.getItem('chatWidget_adminId')!, 10) : null);

        if (currentAdminId) {
          try {
            const conversationResponse = await getConversation(currentAdminId);
            const sortedMessages = [...conversationResponse.messages].sort((a, b) => {
              return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            });
            setMessages(sortedMessages);

            // Guardar nombre del admin
            if (conversationResponse.otherUser) {
              const admin = conversationResponse.otherUser;
              if (admin.role === 'admin') {
                if (admin.name && admin.name !== admin.email) {
                  setAdminName(admin.name);
                } else {
                  setAdminName(null);
                }
              }
            }

            const unreadResponse = await getMessages();
            setUnreadCount(unreadResponse.unreadCount);
          } catch (error) {
            // Si falla, usar getMessages normal
            const response = await getMessages();
            const sortedMessages = [...response.messages].sort((a, b) => {
              return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
            });
            setMessages(sortedMessages);
            setUnreadCount(response.unreadCount);
          }
        } else {
          // Si realmente no hay adminId, usar getMessages normal (solo recibidos)
          const response = await getMessages();
          const sortedMessages = [...response.messages].sort((a, b) => {
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          });
          setMessages(sortedMessages);
          setUnreadCount(response.unreadCount);

          // Intentar encontrar adminId en los mensajes recibidos
          const adminMessage = response.messages.find(msg => msg.fromUser.role === 'admin');
          if (adminMessage) {
            setAdminId(adminMessage.fromUser.id);
          }
        }
      }
    } catch (error) {
      // Error al cargar mensajes - silenciado
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await getMessages();
      setUnreadCount(response.unreadCount);
    } catch (error) {
      // Error al cargar contador de mensajes no leídos - silenciado
    }
  };

  const handleSendMessage = async () => {
    const hasText = messageText.trim();
    const hasFile = selectedFile !== null;

    if ((!hasText && !hasFile) || sending || uploadingFile || !adminId) {
      return;
    }

    try {
    setSending(true);
      setUploadingFile(true);
      setUploadError(null);

      let attachmentUrl: string | undefined;
      let attachmentFilename: string | undefined;
      let attachmentType: string | undefined;

      if (selectedFile) {
        try {
          const isImage = selectedFile.type.startsWith('image/');
          attachmentUrl = await uploadToSupabase(selectedFile, {
            folder: 'perfil-institucional/messages',
          });
          
          // Log para debugging
          
          attachmentFilename = selectedFile.name;
          attachmentType = isImage ? 'image' : 'document';
        } catch (err: any) {
          // Verificar si es un error de rate limiting (429)
          if (handleRateLimitError(err)) {
            setUploadingFile(false);
            setSending(false);
            return;
          }
          
          // Verificar si es un error de formato o tamaño del backend
          if (handleUploadError(err, showFileSizeError)) {
            setUploadingFile(false);
            setSending(false);
            return;
          }
          
          // Verificar si es un error de timeout
          if (err?.isTimeout) {
            showTimeout(err.operationName || 'la subida del archivo');
            setUploadingFile(false);
            setSending(false);
            return;
          }
          
          // Manejar errores 403 (permisos)
          if (err.status === 403 || err.statusCode === 403 || err.message?.includes('403')) {
            setUploadError('Error de permisos al subir archivo. Por favor, contacta al administrador del sistema.');
          } else {
          setUploadError(err.message || 'Error desconocido al subir archivo');
          }
          setUploadingFile(false);
          setSending(false);
          return;
        }
        setUploadingFile(false);
      }

      const messageData: any = {
        toUserId: adminId,
      };

      if (hasText) {
        messageData.message = messageText.trim();
      }

      if (attachmentUrl) {
        messageData.attachmentUrl = attachmentUrl;
        messageData.attachmentFilename = attachmentFilename;
        messageData.attachmentType = attachmentType;
      }

      if (replyToMessageId) {
        messageData.replyToMessageId = replyToMessageId;
      }

      await sendMessage(messageData);

      // Track mensaje enviado
      AnalyticsEvents.chatMessageSent(!!attachmentUrl, attachmentType);

      setMessageText('');
      setSelectedFile(null);
      setFilePreview(null);
      setUploadError(null);
      setReplyToMessageId(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      if (adminId) {
        try {
          const conversationResponse = await getConversation(adminId);
          const sortedMessages = [...conversationResponse.messages].sort((a, b) => {
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          });
          setMessages(sortedMessages);

          if (conversationResponse.otherUser) {
            const admin = conversationResponse.otherUser;
            if (admin.role === 'admin') {
              if (admin.name && admin.name !== admin.email) {
                setAdminName(admin.name);
              } else {
                setAdminName(null);
              }
            }
          }

          const unreadResponse = await getMessages();
          setUnreadCount(unreadResponse.unreadCount);
        } catch (error) {

          await loadMessages();
        }
      } else {
        await findAdminId();
        await loadMessages();
      }
    } catch (error) {

    } finally {
      setSending(false);
      setUploadingFile(false);
    }
  };

  // Función helper para convertir fecha de BD a GMT-3 (sumar 3 horas)
  const parseGMT3Date = (dateString: string): Date => {
    // La BD guarda en UTC (o timezone que está 3 horas atrás)
    // Para mostrar en GMT-3 (Argentina), hay que SUMAR 3 horas
    // Ejemplo: BD tiene 15:00 → mostrar 18:00
    if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      // Si no tiene timezone, asumir UTC y sumar 3 horas para GMT-3
      // Formato esperado: "2024-01-29 15:00:00" o "2024-01-29T15:00:00"
      const dateTime = dateString.replace(' ', 'T');
      // Crear Date interpretando como UTC
      const utcDate = new Date(dateTime + 'Z');
      // SUMAR 3 horas para convertir a GMT-3 (Argentina)
      const gmt3Date = new Date(utcDate.getTime() + (3 * 60 * 60 * 1000));
      return gmt3Date;
    }
    // Si ya tiene timezone, convertir a Date y sumar 3 horas
    const date = new Date(dateString);
    return new Date(date.getTime() + (3 * 60 * 60 * 1000));
  };

  const formatDate = (dateString: string) => {
    const date = parseGMT3Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return 'Hoy';
    } else if (days === 1) {
      return 'Ayer';
    } else if (days < 7) {
      return `Hace ${days} días`;
    } else {
      return date.toLocaleDateString('es-AR', {
        day: 'numeric',
        month: 'short',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
        timeZone: 'America/Argentina/Buenos_Aires' // GMT-3
      });
    }
  };

  const formatTime = (dateString: string) => {
    const date = parseGMT3Date(dateString);
    return date.toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Argentina/Buenos_Aires' // GMT-3
    });
  };

  // Función helper para extraer solo el primer nombre
  const getFirstName = (fullName?: string): string => {
    if (!fullName) return '';
    return fullName.split(' ')[0];
  };

  const handleOpenChat = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Abrir el chat individual con el admin en una nueva pestaña
    window.open('/soporte', '_blank');
  };

  const handleRequestReview = async () => {
    if (!adminId || isSendingReviewRequest) return;

    setIsSendingReviewRequest(true);
    try {
      await sendMessage({
        toUserId: adminId,
        message: 'Solicito revisión de mi perfil'
      });

      // Pequeño delay para asegurar que el mensaje esté guardado
      await new Promise(resolve => setTimeout(resolve, 500));

      // Recargar mensajes para mostrar el mensaje enviado
      if (adminId) {
        try {
          const conversationResponse = await getConversation(adminId);
          const sortedMessages = [...conversationResponse.messages].sort((a, b) => {
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          });
          setMessages(sortedMessages);

          // Guardar nombre del admin
          if (conversationResponse.otherUser) {
            const admin = conversationResponse.otherUser;
            if (admin.role === 'admin') {
              if (admin.name && admin.name !== admin.email) {
                setAdminName(admin.name);
              } else {
                setAdminName(null);
              }
            }
          }

          const unreadResponse = await getMessages();
          setUnreadCount(unreadResponse.unreadCount);
        } catch (error) {

          await loadMessages();
        }
      } else {
        await loadMessages();
      }
    } catch (error) {

      alert('Error al enviar solicitud de revisión. Por favor, intenta nuevamente.');
    } finally {
      setIsSendingReviewRequest(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    const isImage = file.type.startsWith('image/');
    const maxSize = isImage ? MAX_FILE_SIZE_IMAGES : MAX_FILE_SIZE_DOCUMENTS;
    const validation = validateFileSize(file, maxSize, isImage ? 'image' : 'document');

    if (!validation.valid) {
      showFileSizeError(validation.error || 'El archivo excede el tamaño máximo permitido.');
      return;
    }

    setSelectedFile(file);

    if (isImage) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEditMessage = async (messageId: number, newText: string) => {
    if (!newText.trim()) return;

    try {
      await editMessage(messageId, newText.trim());
      setEditingMessageId(null);
      setEditingMessageDimensions(null);
      await loadMessages();
    } catch (err: any) {

    }
  };

  const handleDeleteMessage = (messageId: number) => {
    setMessageToDelete(messageId);
    setShowDeleteModal(true);
  };

  const confirmDeleteMessage = async () => {
    if (!messageToDelete) return;

    try {
      await deleteMessage(messageToDelete);
      setShowDeleteModal(false);
      setMessageToDelete(null);
      await loadMessages();
    } catch (err: any) {

      setShowDeleteModal(false);
      setMessageToDelete(null);
    }
  };

  const handleDownloadFile = async (messageId: number, filename: string) => {
    try {
      const blob = await downloadMessageAttachment(messageId, filename);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 200);
    } catch (error) {

      alert('No se pudo descargar el archivo. Por favor, intenta nuevamente.');
    }
  };

  const getReplyMessage = (replyToId: number | undefined): ConversationMessage | null => {
    if (!replyToId) return null;
    return messages.find(m => m.id === replyToId) as ConversationMessage | null;
  };

  const getFileIcon = (type?: string) => {
    if (!type) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      );
    }
    if (type === 'image') {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <circle cx="8.5" cy="8.5" r="1.5"></circle>
          <polyline points="21 15 16 10 5 21"></polyline>
        </svg>
      );
    }
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10 9 9 9 8 9"></polyline>
      </svg>
    );
  };

  const formatMessageTime = (dateString: string) => {
    const date = parseGMT3Date(dateString);
    return date.toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Argentina/Buenos_Aires' // GMT-3
    });
  };

  const toggleWidget = () => {
    if (isOpen) {
      setIsMinimized(!isMinimized);
    } else {
      setIsOpen(true);
      setIsMinimized(false);
      // Track apertura del chat
      AnalyticsEvents.chatOpened();
    }
  };

  return (
    <div className="chat-widget-container" ref={widgetRef}>
      {isOpen && !isMinimized && (
        <div className="chat-widget-window">
          <div className="chat-widget-header">
            <div className="chat-widget-header-title">
              <h3>Mensajes</h3>
              {/* Solo mostrar el nombre del admin si ya se cargaron los mensajes para evitar parpadeo */}
              {adminName && messages.length > 0 && (
                <span className="chat-widget-header-subtitle">
                  {getFirstName(adminName)} (Soporte)
                </span>
              )}
              {/* Mostrar "Soporte" solo si hay adminId pero aún no se cargaron mensajes */}
              {!adminName && adminId && messages.length === 0 && (
                <span className="chat-widget-header-subtitle">
                  Soporte
                </span>
              )}
            </div>
            <div className="chat-widget-header-actions">
              <button
                ref={openChatButtonRef}
                type="button"
                className="chat-widget-button-icon"
                onClick={handleOpenChat}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                aria-label="Abrir chat completo"
                title="Abrir chat completo"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 17L17 7"></path>
                  <path d="M7 7h10v10"></path>
                </svg>
              </button>
              <button
                type="button"
                className="chat-widget-button-icon"
                onClick={() => setIsMinimized(true)}
                aria-label="Minimizar"
                title="Minimizar"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            </div>
          </div>

          <div className="chat-widget-messages" ref={messagesContainerRef}>
            {loading && messages.length === 0 ? (
              <div className="chat-widget-loading">Cargando mensajes...</div>
            ) : messages.length === 0 ? (
              <div className="chat-widget-empty">No hay mensajes aún</div>
            ) : (
              messages.map((message) => {
                const msg = message as ConversationMessage;
                const isSent = msg.isFromCurrentUser || false;
                const replyTo = getReplyMessage(msg.replyToMessageId);
                const isEditing = editingMessageId === message.id;
                const hasOnlyImage = msg.attachmentUrl && msg.attachmentType === 'image' && !msg.message;

                return (
                  <div
                    key={message.id}
                    className={`chat-widget-message ${isSent ? 'message-sent' : 'message-received'} ${hasOnlyImage ? 'image-only' : ''}`}
                  >
                    <div className={`chat-widget-message-content ${hasOnlyImage ? 'image-only-content' : ''}`}>
                      {replyTo && (
                        <div className="chat-widget-message-reply">
                          <div className="chat-widget-message-reply-line"></div>
                          <div className="chat-widget-message-reply-content">
                            <span className="chat-widget-message-reply-author">
                              {replyTo.isFromCurrentUser 
                                ? 'Tú' 
                                : replyTo.fromUser.role === 'admin'
                                  ? (replyTo.fromUser.name && replyTo.fromUser.name !== replyTo.fromUser.email
                                      ? `${getFirstName(replyTo.fromUser.name)} (Soporte)`
                                      : 'Soporte')
                                  : (getFirstName(replyTo.fromUser.name) || replyTo.fromUser.email)}
                            </span>
                            <span className="chat-widget-message-reply-text">
                              {replyTo.attachmentUrl 
                                ? `${getFileIcon(replyTo.attachmentType)} ${replyTo.attachmentFilename || 'Archivo adjunto'}`
                                : replyTo.message || 'Mensaje eliminado'}
                            </span>
                          </div>
                        </div>
                      )}

                      {isEditing ? (
                        <>
                          {msg.attachmentUrl && (
                            <div className="chat-widget-message-attachment">
                              {msg.attachmentType === 'image' ? (
                                <img 
                                  src={msg.attachmentUrl} 
                                  alt={msg.attachmentFilename || 'Imagen adjunta'}
                                  className="chat-widget-message-image"
                                />
                              ) : (
                                <div className="chat-widget-message-file">
                                  {getFileIcon(msg.attachmentType)} 
                                  <span className="chat-widget-message-filename">{msg.attachmentFilename || 'Archivo adjunto'}</span>
                                </div>
                              )}
                            </div>
                          )}
                          <div className="chat-widget-message-edit">
                            <textarea
                              ref={editTextareaRef}
                              className="chat-widget-message-edit-input"
                              defaultValue={msg.message || ''}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  handleEditMessage(message.id, e.currentTarget.value);
                                }
                                if (e.key === 'Escape') {
                                  setEditingMessageId(null);
                                  setEditingMessageDimensions(null);
                                }
                              }}
                              onBlur={(e) => {
                                if (e.currentTarget.value.trim() !== msg.message) {
                                  handleEditMessage(message.id, e.currentTarget.value);
                                } else {
                                  setEditingMessageId(null);
                                  setEditingMessageDimensions(null);
                                }
                              }}
                            />
                            <div className="chat-widget-message-edit-hint">Enter para guardar, Esc para cancelar</div>
                          </div>
                        </>
                      ) : (
                        <>
                          {msg.attachmentUrl && (
                            <div className="chat-widget-message-attachment">
                              {msg.attachmentType === 'image' ? (
                                <img 
                                  src={msg.attachmentUrl} 
                                  alt={msg.attachmentFilename || 'Imagen adjunta'}
                                  className="chat-widget-message-image"
                                />
                              ) : (
                                <div className="chat-widget-message-file">
                                  {getFileIcon(msg.attachmentType)} 
                                  <span className="chat-widget-message-filename">{msg.attachmentFilename || 'Archivo adjunto'}</span>
                                </div>
                              )}
                            </div>
                          )}
                          {msg.message && (
                            <div className="chat-widget-message-text">{msg.message}</div>
                          )}
                        </>
                      )}

                      <div className="chat-widget-message-footer">
                      <div className="chat-widget-message-time">
                          {formatMessageTime(message.createdAt)}
                          {msg.edited && <span className="chat-widget-message-edited"> (editado)</span>}
                          {isSent && msg.delivered && (
                            <span className="chat-widget-message-checkmark">
                              {msg.seenAt ? '✓✓' : '✓'}
                            </span>
                          )}
                      </div>
                        {isSent && !isEditing && (
                          <div className="chat-widget-message-actions">
                            <button
                              type="button"
                              className="chat-widget-message-action-btn"
                              onClick={(e) => {
                                // Capturar las dimensiones del mensaje original antes de activar la edición
                                const messageContent = (e.currentTarget.closest('.chat-widget-message')?.querySelector('.chat-widget-message-content') as HTMLElement);
                                if (messageContent) {
                                  // Usar getBoundingClientRect() para obtener las dimensiones reales renderizadas
                                  const rect = messageContent.getBoundingClientRect();
                                  // El ancho y alto del contenedor completo, menos el padding (12px a cada lado = 24px total)
                                  const padding = 24;
                                  const contentWidth = Math.max(rect.width - padding, 150); // mínimo 150px
                                  const contentHeight = rect.height;

                                  setEditingMessageDimensions({ 
                                    width: contentWidth, 
                                    height: contentHeight 
                                  });
                                }
                                setEditingMessageId(message.id);
                              }}
                              title="Editar"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="chat-widget-message-action-btn"
                              onClick={() => handleDeleteMessage(message.id)}
                              title="Eliminar"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </button>
                          </div>
                        )}
                        {!isSent && (
                          <div className="chat-widget-message-actions">
                            {msg.attachmentUrl && (
                              <button
                                type="button"
                                className="chat-widget-message-action-btn"
                                onClick={() => handleDownloadFile(
                                  message.id,
                                  msg.attachmentFilename || (msg.attachmentType === 'image' ? 'imagen' : 'archivo')
                                )}
                                title="Descargar"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                  <polyline points="7 10 12 15 17 10"></polyline>
                                  <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                              </button>
                            )}
                            <button
                              type="button"
                              className="chat-widget-message-action-btn"
                              onClick={() => setReplyToMessageId(message.id)}
                              title="Responder"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="9 10 4 15 9 20"></polyline>
                                <path d="M20 4v7a4 4 0 0 1-4 4H4"></path>
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-widget-input-container">
            {replyToMessageId && (() => {
              const replyMessage = getReplyMessage(replyToMessageId);
              return replyMessage && (
                <div className="chat-widget-reply-preview">
                  <div className="chat-widget-reply-preview-content">
                    <span className="chat-widget-reply-preview-label">Respondiendo a:</span>
                    {replyMessage.attachmentUrl ? (
                      replyMessage.attachmentType === 'image' ? (
                        <div className="chat-widget-reply-preview-attachment">
                          <img 
                            src={replyMessage.attachmentUrl} 
                            alt={replyMessage.attachmentFilename || 'Imagen adjunta'}
                            className="chat-widget-reply-preview-image"
                          />
                          <span className="chat-widget-reply-preview-filename">
                            {replyMessage.attachmentFilename || 'Imagen adjunta'}
                          </span>
                        </div>
                      ) : (
                        <div className="chat-widget-reply-preview-attachment">
                          {getFileIcon(replyMessage.attachmentType)}
                          <span className="chat-widget-reply-preview-filename">
                            {replyMessage.attachmentFilename || 'Archivo adjunto'}
                          </span>
                        </div>
                      )
                    ) : (
                      <span className="chat-widget-reply-preview-text">
                        {replyMessage.message || 'Mensaje eliminado'}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="chat-widget-reply-preview-close"
                    onClick={() => setReplyToMessageId(null)}
                  >
                    ×
                  </button>
                </div>
              );
            })()}
            {filePreview && (
              <div className="chat-widget-file-preview">
                <img src={filePreview} alt="Preview" className="chat-widget-file-preview-image" />
                <button
                  type="button"
                  className="chat-widget-file-preview-remove"
                  onClick={removeFile}
                >
                  ×
                </button>
              </div>
            )}
            {selectedFile && !filePreview && (
              <div className="chat-widget-file-preview">
                <span className="chat-widget-file-preview-name">
                  {getFileIcon(selectedFile.type.startsWith('image/') ? 'image' : 'document')} {selectedFile.name}
                </span>
                <button
                  type="button"
                  className="chat-widget-file-preview-remove"
                  onClick={removeFile}
                >
                  ×
                </button>
              </div>
            )}
            {uploadError && (
              <div className="chat-widget-upload-error">
                <span className="chat-widget-upload-error-text">⚠️ {uploadError}</span>
                <button
                  type="button"
                  className="chat-widget-upload-error-close"
                  onClick={() => setUploadError(null)}
                  title="Cerrar"
                >
                  ×
                </button>
              </div>
            )}
          <div className="chat-widget-input">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="chat-widget-file-input"
              />
              <button
                type="button"
                className="chat-widget-attach-button"
                onClick={() => fileInputRef.current?.click()}
                title="Adjuntar archivo"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                </svg>
              </button>
            <textarea
              value={messageText}
                onChange={(e) => {
                  setMessageText(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                onPaste={async (e) => {
                  const items = e.clipboardData.items;
                  for (let i = 0; i < items.length; i++) {
                    const item = items[i];
                    if (item.type.indexOf('image') !== -1) {
                      e.preventDefault();
                      const blob = item.getAsFile();
                      if (blob) {
                        const timestamp = new Date().getTime();
                        const fileExtension = blob.type.split('/')[1] || 'png';
                        const fileName = `imagen-${timestamp}.${fileExtension}`;
                        const file = new File([blob], fileName, { type: blob.type });
                        const isImage = file.type.startsWith('image/');
                        const maxSize = isImage ? MAX_FILE_SIZE_IMAGES : MAX_FILE_SIZE_DOCUMENTS;
                        const validation = validateFileSize(file, maxSize);
                        if (!validation.valid) {
                          showFileSizeError(validation.error || 'El archivo es demasiado grande');
                          return;
                        }
                        setSelectedFile(file);
                        if (isImage) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setFilePreview(reader.result as string);
                          };
                          reader.readAsDataURL(file);
                        }
                        setUploadError(null);
                      }
                      break;
                    }
                  }
                }}
              placeholder="Escribe un mensaje..."
                rows={1}
                disabled={sending || uploadingFile || !adminId}
            />
            <button
              type="button"
              onClick={handleSendMessage}
                disabled={(!messageText.trim() && !selectedFile) || sending || uploadingFile || !adminId}
              className="chat-widget-send-button"
            >
                {sending || uploadingFile ? '...' : '→'}
            </button>
          </div>
          </div>
          <Modal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setMessageToDelete(null);
            }}
            onConfirm={confirmDeleteMessage}
            confirmText="Aceptar"
            cancelText="Cancelar"
          >
            <p>¿Estás seguro de que deseas eliminar este mensaje?</p>
          </Modal>
        </div>
      )}

      <button
        type="button"
        className="chat-widget-toggle"
        onClick={toggleWidget}
        aria-label="Abrir mensajes"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
        {unreadCount > 0 && (
          <span className="chat-widget-badge">{unreadCount}</span>
        )}
      </button>

      <button
        type="button"
        onClick={handleRequestReview}
        disabled={isSendingReviewRequest || !adminId}
        className="chat-widget-review-button-external"
        title="Solicitar revisión del perfil"
      >
        {isSendingReviewRequest ? 'Enviando...' : 'Solicitar revisión del perfil'}
      </button>
    </div>
  );
};

export default ChatWidget;
